// Delega generale (tutti i servizi e le utenze, valida fino a revoca): si usa come la Delega del Canone RAI.
// Stampa → il cliente firma nel riquadro → si carica la scansione (firma ritagliata e salvata in anagrafica).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { FileSignature, Printer, Upload, Eye, Ban, Loader2, Check, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/deleghe-generali`;
const data = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '');
const aggiornaElenco = () => window.dispatchEvent(new Event('jvservice:deleghe-generali'));

const apriPdf = async (url) => {
  const finestra = window.open('', '_blank');
  try {
    const r = await axios.get(url, { responseType: 'blob' });
    const u = URL.createObjectURL(r.data);
    if (finestra) finestra.location.href = u; else window.location.href = u;
  } catch (err) { if (finestra) finestra.close(); toast.error('Documento non disponibile'); }
};

export const StatoDelega = ({ d }) => (
  d.stato === 'firmata' ? <Badge className="bg-green-100 text-green-800"><Check className="w-3.5 h-3.5 mr-1" />Firmata il {data(d.firmata_il)}</Badge>
    : d.stato === 'revocata' ? <Badge className="bg-slate-200 text-slate-700"><Ban className="w-3.5 h-3.5 mr-1" />Revocata il {data(d.revocata_il)}</Badge>
      : <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3.5 h-3.5 mr-1" />Da firmare</Badge>
);

// Riquadro nella pagina Delega per il cliente scelto
export function DelegaGeneraleCliente({ client, onCambiata, onFirmata }) {
  const [d, setD] = useState(undefined);       // undefined = carico, null = nessuna delega in vigore
  const [attesa, setAttesa] = useState('');
  const fileRef = useRef(null);
  const carica = useCallback(() => {
    axios.get(`${API}/cliente/${client.id}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [client.id]);
  useEffect(() => { setD(undefined); carica(); }, [carica]);

  const prepara = async () => {
    setAttesa('prepara');
    try {
      const r = await axios.post(API, { client_id: client.id });
      setD(r.data); aggiornaElenco(); onCambiata?.();
      await apriPdf(`${API}/${r.data.id}/pdf`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Delega non creata'); } finally { setAttesa(''); }
  };
  const caricaFirmata = async (file) => {
    if (!file) return;
    setAttesa('firmata');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/${d.id}/firmata`, fd);
      r.data.riquadro_trovato ? toast.success(r.data.messaggio) : toast.warning(r.data.messaggio);
      carica(); aggiornaElenco(); onCambiata?.(); onFirmata?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Caricamento non riuscito'); } finally { setAttesa(''); }
  };
  const revoca = async () => {
    const motivo = window.prompt('Revocare la delega generale? Scrivi il motivo (facoltativo):', 'richiesta del cliente');
    if (motivo === null) return;
    try { await axios.post(`${API}/${d.id}/revoca`, { motivo }); toast.success('Delega revocata'); carica(); aggiornaElenco(); onCambiata?.(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Non revocata'); }
  };

  return (
    <div className="p-4 rounded-lg border border-violet-200 bg-violet-50/50 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <FileSignature className="w-4 h-4 text-violet-700" />
        <span className="font-semibold text-slate-800">Delega generale — tutti i servizi e le utenze</span>
        {d && <StatoDelega d={d} />}
      </div>
      <p className="text-xs text-slate-600">Vale per Comune, Questura, Prefettura, INPS, INAIL, Agenzia delle Entrate e fornitori di luce, gas, acqua,
        telefonia e internet, fino a revoca scritta. Il cliente firma una volta sola.</p>
      {d === undefined ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
        <div className="flex flex-wrap gap-2">
          {!d ? (
            <Button size="sm" onClick={prepara} disabled={!!attesa} className="bg-violet-700 hover:bg-violet-800">
              {attesa === 'prepara' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />} Prepara delega generale
            </Button>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => apriPdf(`${API}/${d.id}/pdf`)}><Printer className="w-4 h-4 mr-1" /> Stampa da firmare</Button>
              <Button size="sm" onClick={() => fileRef.current?.click()} disabled={!!attesa}
                className={d.stato === 'firmata' ? 'bg-white text-slate-800 border hover:bg-slate-50' : 'bg-slate-900 hover:bg-slate-800'}>
                {attesa === 'firmata' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {d.stato === 'firmata' ? 'Ricarica firmata' : 'Carica delega firmata (estrae firma)'}
              </Button>
              {d.stato === 'firmata' && <Button size="sm" variant="outline" onClick={() => apriPdf(`${API}/${d.id}/firmata`)}><Eye className="w-4 h-4 mr-1" /> Vedi firmata</Button>}
              <Button size="sm" variant="ghost" className="text-red-600" onClick={revoca}><Ban className="w-4 h-4 mr-1" /> Revoca</Button>
              <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden"
                onChange={(e) => { caricaFirmata(e.target.files?.[0]); e.target.value = ''; }} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Elenco di tutte le deleghe generali (attive e revocate: la cronologia resta)
export function ElencoDelegheGenerali() {
  const [items, setItems] = useState(null);
  const carica = useCallback(() => {
    axios.get(API).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);
  useEffect(() => {
    carica();
    window.addEventListener('jvservice:deleghe-generali', carica);
    return () => window.removeEventListener('jvservice:deleghe-generali', carica);
  }, [carica]);
  return (
    <Card>
      <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileSignature className="w-4 h-4 text-violet-700" /> Deleghe generali</CardTitle></CardHeader>
      <CardContent className="p-0">
        {items === null ? <div className="p-4"><Loader2 className="w-4 h-4 animate-spin" /></div>
          : items.length === 0 ? <p className="p-4 text-sm text-slate-500">Nessuna delega generale.</p>
            : (
              <div className="divide-y max-h-80 overflow-y-auto">
                {items.map((d) => (
                  <div key={d.id} className="p-3 text-sm flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{d.client_name}</div>
                      <div className="text-xs text-slate-500">creata il {data(d.creata_il)}{d.motivo_revoca ? ` · ${d.motivo_revoca}` : ''}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <StatoDelega d={d} />
                      {d.firmata_il && <button title="Vedi delega firmata" onClick={() => apriPdf(`${API}/${d.id}/firmata`)} className="p-1 text-blue-700"><Eye className="w-4 h-4" /></button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
      </CardContent>
    </Card>
  );
}

// Finestra con la delega generale di un cliente (dalle pratiche: Canone RAI, Ospitalità, Assegno di maternità, ...)
export function FinestraDelegaGenerale({ clientId, clientName, onChiudi, onCambiata, onFirmata }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="w-[95vw] sm:max-w-xl overflow-hidden">
        <DialogHeader><DialogTitle>Delega generale{clientName ? ` — ${clientName}` : ''}</DialogTitle></DialogHeader>
        <DelegaGeneraleCliente client={{ id: clientId }} onCambiata={onCambiata} onFirmata={onFirmata} />
      </DialogContent>
    </Dialog>
  );
}

// Pulsantino sulla riga della pratica: stato della delega generale del cliente, clic = finestra
export function DelegaGeneraleBadge({ clientId, clientName, onFirmata }) {
  const [d, setD] = useState(undefined);
  const [aperta, setAperta] = useState(false);
  const carica = useCallback(() => {
    if (!clientId) return;
    axios.get(`${API}/cliente/${clientId}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [clientId]);
  useEffect(() => { carica(); }, [carica]);
  if (!clientId) return null;
  const stile = d?.stato === 'firmata' ? 'bg-green-50 border-green-200 text-green-800'
    : d?.stato === 'da_firmare' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50';
  // span che ferma i clic: anche quelli dentro la finestra (portale React) risalirebbero fino alla riga della
  // pratica, che in alcune pagine apre "Modifica pratica" al clic (es. Assegno di maternità)
  return (
    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => setAperta(true)}
        title="Delega generale del cliente (tutti i servizi, fino a revoca)"
        className={`inline-flex items-center gap-1 h-6 px-2 rounded-md border text-[11px] font-medium whitespace-nowrap ${stile}`}>
        {d?.stato === 'firmata' ? <Check className="w-3 h-3" /> : <FileSignature className="w-3 h-3" />}
        {d === undefined ? 'Delega…' : d?.stato === 'firmata' ? 'Delega firmata' : d?.stato === 'da_firmare' ? 'Delega da firmare' : 'Delega'}
      </button>
      {aperta && <FinestraDelegaGenerale clientId={clientId} clientName={clientName} onChiudi={() => { setAperta(false); carica(); }}
        onCambiata={carica} onFirmata={onFirmata} />}
    </span>
  );
}
